import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { ArrowsClockwise, CalendarCheck, Trash, Warning, PencilSimple, X } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";

const WEEKDAYS = [
  { i: 0, l: "Mon" }, { i: 1, l: "Tue" }, { i: 2, l: "Wed" }, { i: 3, l: "Thu" },
  { i: 4, l: "Fri" }, { i: 5, l: "Sat" }, { i: 6, l: "Sun" },
];
const WORK_TYPES = [
  { key: "regular", label: "Regular Hours" },
  { key: "paid_duty", label: "Paid Duty" },
  { key: "court", label: "Court" },
  { key: "overtime", label: "Overtime" },
  { key: "event", label: "Other event" },
];
const today = () => format(new Date(), "yyyy-MM-dd");

export default function RecurringScheduleDialog({ open, onOpenChange, currency = "CAD" }) {
  const qc = useQueryClient();
  const [ruleType, setRuleType] = useState("weekly");
  const [workType, setWorkType] = useState("regular");
  const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4]);
  const [endMode, setEndMode] = useState("end_date"); // end_date | occurrences
  const [f, setF] = useState({
    hours: "8", label: "", start_time: "08:00", hourly_rate: "", multiplier: "1.5", note: "",
    on_block: "4", off_block: "4", anchor_date: today(), start_date: today(), end_date: "", occurrences: "",
  });
  const [preview, setPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const upd = (k, v) => { setF((p) => ({ ...p, [k]: v })); setPreview(null); };

  const resetForm = () => {
    setEditingId(null);
    setRuleType("weekly");
    setWorkType("regular");
    setWeekdays([0, 1, 2, 3, 4]);
    setEndMode("end_date");
    setF({
      hours: "8", label: "", start_time: "08:00", hourly_rate: "", multiplier: "1.5", note: "",
      on_block: "4", off_block: "4", anchor_date: today(), start_date: today(), end_date: "", occurrences: "",
    });
    setPreview(null);
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setRuleType(s.rule_type);
    setWorkType(s.work_type);
    setWeekdays(s.weekdays && s.weekdays.length ? s.weekdays : [0, 1, 2, 3, 4]);
    setEndMode(s.occurrences ? "occurrences" : "end_date");
    setF({
      hours: String(s.hours ?? "8"),
      label: s.label || "",
      start_time: s.start_time || "08:00",
      hourly_rate: s.hourly_rate ? String(s.hourly_rate) : "",
      multiplier: s.multiplier ? String(s.multiplier) : "1.5",
      note: s.note || "",
      on_block: String(s.on_block ?? "4"),
      off_block: String(s.off_block ?? "4"),
      anchor_date: s.anchor_date || today(),
      start_date: s.start_date || today(),
      end_date: s.end_date || "",
      occurrences: s.occurrences ? String(s.occurrences) : "",
    });
    setPreview(null);
  };

  const { data: series = [] } = useQuery({
    queryKey: ["recurring"],
    queryFn: async () => (await api.get("/recurring")).data,
    enabled: open,
  });

  const buildRule = () => {
    const r = {
      rule_type: ruleType, work_type: workType,
      hours: parseFloat(f.hours) || 0, label: f.label, start_time: f.start_time,
      hourly_rate: parseFloat(f.hourly_rate) || 0, multiplier: parseFloat(f.multiplier) || 1.5,
      note: f.note, start_date: f.start_date,
    };
    if (ruleType === "weekly") {
      r.weekdays = weekdays;
      if (endMode === "end_date") r.end_date = f.end_date || null;
      else r.occurrences = parseInt(f.occurrences) || null;
    } else {
      r.on_block = parseInt(f.on_block) || 0;
      r.off_block = parseInt(f.off_block) || 0;
      r.anchor_date = f.anchor_date;
      r.end_date = f.end_date || null;
    }
    return r;
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["recurring"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["projections"] });
    qc.invalidateQueries({ queryKey: ["calendar"] });
  };

  const previewMut = useMutation({
    mutationFn: async () => (await api.post(`/recurring/preview${editingId ? `?exclude_series=${editingId}` : ""}`, buildRule())).data,
    onSuccess: (d) => setPreview(d),
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/recurring", buildRule())).data,
    onSuccess: (d) => {
      invalidate();
      toast.success(`Created ${d.created} scheduled entries${d.skipped ? ` (${d.skipped} skipped)` : ""}.`);
      setPreview(null);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const updateMut = useMutation({
    mutationFn: async () => (await api.put(`/recurring/${editingId}`, buildRule())).data,
    onSuccess: (d) => {
      invalidate();
      toast.success(`Series updated — ${d.created} entries scheduled${d.skipped ? ` (${d.skipped} skipped)` : ""}.`);
      resetForm();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/recurring/${id}`)).data,
    onSuccess: (d, id) => {
      invalidate();
      if (id === editingId) resetForm();
      toast.success(`Series deleted (${d.deleted} scheduled entries removed).`);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const toggleWeekday = (i) => {
    setPreview(null);
    setWeekdays((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i].sort()));
  };

  const needsPay = workType === "paid_duty" || workType === "court";

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent
        className="bg-popover border-border max-w-lg max-h-[88vh] overflow-y-auto"
        data-testid="recurring-dialog"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowsClockwise className="h-5 w-5 text-primary" weight="bold" /> Recurring Schedule
          </DialogTitle>
          <DialogDescription className="text-xs">Generate scheduled entries automatically. Existing dates are never overwritten.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {editingId && (
            <div className="flex items-center justify-between gap-2 rounded-md bg-primary/10 border border-primary/30 px-3 py-2" data-testid="recurring-edit-banner">
              <span className="text-xs font-semibold text-primary flex items-center gap-1.5">
                <PencilSimple className="h-3.5 w-3.5" weight="bold" /> Editing existing schedule — saving replaces its remaining scheduled entries.
              </span>
              <button onClick={resetForm} data-testid="recurring-cancel-edit"
                className="text-muted-foreground hover:text-foreground transition-colors duration-200 shrink-0">
                <X className="h-4 w-4" weight="bold" />
              </button>
            </div>
          )}
          {/* Rule type */}
          <div className="grid grid-cols-2 gap-2">
            {[["weekly", "Weekly by weekday"], ["rotation", "Rotation (N on / M off)"]].map(([k, l]) => (
              <button key={k} onClick={() => { setRuleType(k); setPreview(null); }}
                data-testid={`ruletype-${k}`}
                className={`px-3 py-2 rounded-md text-sm font-semibold border transition-colors duration-200 ${
                  ruleType === k ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                {l}
              </button>
            ))}
          </div>

          {/* Work type */}
          <div>
            <Label className="text-xs">Work type</Label>
            <Select value={workType} onValueChange={(v) => { setWorkType(v); setPreview(null); }}>
              <SelectTrigger className="mt-1 bg-secondary/50" data-testid="recurring-worktype"><SelectValue /></SelectTrigger>
              <SelectContent>{WORK_TYPES.map((w) => <SelectItem key={w.key} value={w.key}>{w.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {/* Type-specific */}
          {(needsPay || workType === "event") && (
            <Input data-testid="recurring-label" className="bg-secondary/50"
              placeholder={workType === "court" ? "Court name" : workType === "event" ? "Event title" : "Client"}
              value={f.label} onChange={(e) => upd("label", e.target.value)} />
          )}
          {workType !== "event" && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Hours / day</Label>
                <Input type="number" step="0.25" min="0" data-testid="recurring-hours" className="mt-1 bg-secondary/50"
                  value={f.hours} onChange={(e) => upd("hours", e.target.value)} /></div>
              {needsPay && (
                <div><Label className="text-xs">Rate ({currency})</Label>
                  <Input type="number" step="0.01" min="0" data-testid="recurring-rate" className="mt-1 bg-secondary/50"
                    value={f.hourly_rate} onChange={(e) => upd("hourly_rate", e.target.value)} /></div>
              )}
              {workType === "overtime" && (
                <div><Label className="text-xs">Multiplier</Label>
                  <Select value={f.multiplier} onValueChange={(v) => upd("multiplier", v)}>
                    <SelectTrigger className="mt-1 bg-secondary/50" data-testid="recurring-multiplier"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="1.5">1.5×</SelectItem><SelectItem value="2">2×</SelectItem></SelectContent>
                  </Select></div>
              )}
            </div>
          )}
          {needsPay && (
            <div><Label className="text-xs">Start time (end auto-calculated from hours)</Label>
              <Input type="time" data-testid="recurring-start-time" className="mt-1 bg-secondary/50"
                value={f.start_time} onChange={(e) => upd("start_time", e.target.value)} /></div>
          )}

          {/* Weekly params */}
          {ruleType === "weekly" && (
            <>
              <div>
                <Label className="text-xs">Weekdays</Label>
                <div className="flex flex-wrap gap-1.5 mt-1" data-testid="weekday-picker">
                  {WEEKDAYS.map((d) => (
                    <button key={d.i} onClick={() => toggleWeekday(d.i)} data-testid={`weekday-${d.i}`}
                      className={`h-9 w-11 rounded-md text-xs font-semibold border transition-colors duration-200 ${
                        weekdays.includes(d.i) ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"}`}>
                      {d.l}
                    </button>
                  ))}
                </div>
              </div>
              <div><Label className="text-xs">Start date</Label>
                <div className="mt-1"><DatePicker testId="recurring-start-date" value={f.start_date} onChange={(d) => upd("start_date", d)} /></div></div>
              <div className="grid grid-cols-2 gap-2">
                {[["end_date", "End date"], ["occurrences", "# Occurrences"]].map(([k, l]) => (
                  <button key={k} onClick={() => { setEndMode(k); setPreview(null); }} data-testid={`endmode-${k}`}
                    className={`px-3 py-2 rounded-md text-xs font-semibold border transition-colors duration-200 ${
                      endMode === k ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>{l}</button>
                ))}
              </div>
              {endMode === "end_date" ? (
                <div><Label className="text-xs">End date</Label>
                  <div className="mt-1"><DatePicker testId="recurring-end-date" value={f.end_date} onChange={(d) => upd("end_date", d)} /></div></div>
              ) : (
                <Input type="number" min="1" data-testid="recurring-occurrences" className="bg-secondary/50" placeholder="e.g. 20"
                  value={f.occurrences} onChange={(e) => upd("occurrences", e.target.value)} />
              )}
            </>
          )}

          {/* Rotation params */}
          {ruleType === "rotation" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">On-block (days)</Label>
                  <Input type="number" min="1" data-testid="recurring-on-block" className="mt-1 bg-secondary/50"
                    value={f.on_block} onChange={(e) => upd("on_block", e.target.value)} /></div>
                <div><Label className="text-xs">Off-block (days)</Label>
                  <Input type="number" min="0" data-testid="recurring-off-block" className="mt-1 bg-secondary/50"
                    value={f.off_block} onChange={(e) => upd("off_block", e.target.value)} /></div>
              </div>
              <div><Label className="text-xs">Anchor / start date</Label>
                <div className="mt-1"><DatePicker testId="recurring-anchor-date" value={f.anchor_date}
                  onChange={(d) => { upd("anchor_date", d); upd("start_date", d); }} /></div></div>
              <div><Label className="text-xs">End date</Label>
                <div className="mt-1"><DatePicker testId="recurring-rotation-end" value={f.end_date} onChange={(d) => upd("end_date", d)} /></div></div>
            </>
          )}

          {/* Preview + confirm */}
          {preview && (
            <div className="rounded-md bg-secondary/50 border border-border p-3 text-sm" data-testid="recurring-preview">
              {preview.count > 0 ? (
                <p>This will create <span className="font-bold text-primary" data-testid="preview-count">{preview.count}</span> entries between{" "}
                  <span className="font-semibold">{format(new Date(preview.first_date + "T00:00:00"), "MMM d, yyyy")}</span> and{" "}
                  <span className="font-semibold">{format(new Date(preview.last_date + "T00:00:00"), "MMM d, yyyy")}</span>.
                  {preview.skipped > 0 && <span className="text-muted-foreground"> ({preview.skipped} existing dates skipped)</span>}
                  {preview.capped && <span className="block text-amber-400 text-xs mt-1"><Warning className="inline h-3 w-3" /> Capped at 1000 entries — shorten the range.</span>}
                  {!preview.capped && preview.count > 200 && <span className="block text-amber-400 text-xs mt-1" data-testid="preview-long-range"><Warning className="inline h-3 w-3" /> That's a lot of entries — double-check your date range.</span>}
                </p>
              ) : (
                <p className="text-muted-foreground">No new entries — all matching dates already have entries.</p>
              )}
            </div>
          )}

          <div className="flex gap-2">
            {!preview ? (
              <Button onClick={() => previewMut.mutate()} disabled={previewMut.isPending} data-testid="recurring-preview-button"
                className="flex-1 rounded-md font-semibold">
                {previewMut.isPending ? "Calculating…" : "Preview"}
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setPreview(null)} className="rounded-md flex-1">Back</Button>
                <Button onClick={() => (editingId ? updateMut.mutate() : createMut.mutate())}
                  disabled={(editingId ? updateMut.isPending : createMut.isPending) || preview.count === 0}
                  data-testid="recurring-confirm-button" className="rounded-md flex-1 font-semibold">
                  <CalendarCheck className="h-4 w-4 mr-1.5" weight="bold" />
                  {editingId
                    ? (updateMut.isPending ? "Updating…" : "Update Series")
                    : (createMut.isPending ? "Creating…" : `Confirm & Create`)}
                </Button>
              </>
            )}
          </div>

          {/* Existing series */}
          {series.length > 0 && (
            <div className="pt-3 border-t border-border">
              <p className="overline text-muted-foreground mb-2">Your recurring schedules</p>
              <div className="space-y-2" data-testid="recurring-series-list">
                {series.map((s) => (
                  <div key={s.id} data-testid={`series-row-${s.id}`}
                    className={`flex items-center justify-between gap-3 p-2.5 rounded-md bg-secondary/40 ${editingId === s.id ? "ring-1 ring-primary/50" : ""}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium capitalize truncate">
                        {s.rule_type === "weekly" ? "Weekly" : `${s.on_block} on / ${s.off_block} off`} · {s.work_type.replace("_", " ")}
                      </p>
                      <p className="text-xs text-muted-foreground">{s.scheduled_remaining} scheduled remaining</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => startEdit(s)} data-testid={`edit-series-${s.id}`}
                        className="text-muted-foreground hover:text-primary transition-colors duration-200">
                        <PencilSimple className="h-4 w-4" weight="bold" />
                      </button>
                      <button onClick={() => deleteMut.mutate(s.id)} data-testid={`delete-series-${s.id}`}
                        className="text-muted-foreground hover:text-destructive transition-colors duration-200">
                        <Trash className="h-4 w-4" weight="bold" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
